import BaseBranding from "%/dynamic-component-editor/classes/branding/base-branding";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useBrandingStore = (...args) => require("%/dynamic-component-editor/stores/branding-store").default(...args);

export default class ButtonBranding extends BaseBranding {
  static inheritanceStructure = {
    base: "primary.standard",
    inherits: {
      "primary.small": ["primary.standard"],
      "primary.large": ["primary.standard"],
      "secondary.standard": ["primary.standard"],
      "secondary.small": ["primary.standard", "secondary.standard"],
      "secondary.large": ["primary.standard", "secondary.standard"],
      "tertiary.standard": ["primary.standard"],
      "tertiary.small": ["primary.standard", "tertiary.standard"],
      "tertiary.large": ["primary.standard", "tertiary.standard"],
    },
  };

  // Metadata consumed by the Studio Branding section. See
  // `TypographyBranding` for the contract — the sidebar walks
  // `BaseBranding.allProviders` and reads these fields to build
  // menu entries and preview specs.
  static categoryKey = "buttons";
  static categoryTitle = "Buttons";
  static categoryDescription = "Edit the primary, secondary, and tertiary button styles in their standard, small, and large sizes.";

  static previewKind = "button";
  static previewSampleText = "Button";

  // Element type stored on each seeded BrandingElement row — the
  // runtime element class users actually drop on the canvas. The
  // branding config table keys this as `ButtonBranding`, but the
  // resolver and the inspector use the same type identity as
  // canvas instances (`ButtonElement`) — `determineType()` below
  // bridges the two by always returning `"ButtonBranding"`.
  static elementTypes = [
    { type: "ButtonElement", label: "Button" },
  ];

  // Inspector treats every variant's `.standard` size as a self-contained
  // editable root: `primary.standard` is the global base; `secondary.standard`
  // and `tertiary.standard` are conceptually their own variant roots even
  // though the render-time inheritance chain walks them up to
  // `primary.standard`. This keeps "inheriting" / "overrides" badges off
  // them in the Branding inspector while leaving the rendering inheritance
  // graph (`inheritanceStructure.inherits`) intact.
  static get inspectorBaseVariants() {
    return [
      "primary.standard",
      "secondary.standard",
      "tertiary.standard",
    ];
  }

  static get variants() {
    return ["primary", "secondary", "tertiary"];
  }

  static get sizes() {
    return ["standard", "small", "large"];
  }

  // Branding-config top-level key used by the legacy
  // `useBrandingStore().configuration` / `defaultTemplateConfiguration`
  // tables. For buttons there's only one slice and it lives under
  // `ButtonBranding` regardless of which `elementType` the seeded
  // entity ends up as.
  static get brandingConfigKey() {
    return "ButtonBranding";
  }

  static isThemableOptionKey(optionKey) {
    const keys = Object.keys(useBrandingStore().defaultTemplateConfiguration["ButtonBranding"]["primary"]["standard"]);
    return keys.includes(`default.${optionKey}`);
  }

  static getPreviewSpecs() {
    const specs = [];
    for (const { type, label } of this.elementTypes) {
      for (const variant of this.variants) {
        for (const size of this.sizes) {
          specs.push({
            key: `${type}|${variant}|${size}`,
            label: `${label} / ${this.titleCase(variant)} / ${this.titleCase(size)}`,
            elementType: type,
            brandedVariant: variant,
            brandedSize: size,
            brandingClass: this,
          });
        }
      }
    }
    return specs;
  }

  // One section per variant (Primary / Secondary / Tertiary), each
  // holding that variant's size cards — mirrors Typography's
  // per-type grouping so the variants read as distinct blocks
  // instead of one flat run of nine look-alike cards. Order follows
  // `variants`.
  static getPreviewGroups() {
    const specsByVariant = new Map();
    for (const spec of this.getPreviewSpecs()) {
      if (!specsByVariant.has(spec.brandedVariant)) {
        specsByVariant.set(spec.brandedVariant, []);
      }
      specsByVariant.get(spec.brandedVariant).push(spec);
    }
    return this.variants.map(variant => ({
      heading: this.titleCase(variant),
      previews: specsByVariant.get(variant) ?? [],
    }));
  }

  static ownsElementType(elementType) {
    return this.elementTypes.some(entry => entry.type === elementType);
  }

  // Walk one hop up the inheritance chain. `inheritanceStructure.inherits`
  // entries are ordered root → immediate parent, so the LAST element
  // is the next level up. Returning the immediate parent (rather than
  // jumping straight to the base) lets the walker visit each level in
  // turn — `secondary.small → secondary.standard → primary.standard` —
  // so an override at any rung of the chain wins over the base.
  //
  // Coords with no entry in `inherits` (the base or anything unknown)
  // return the base; the walker's same-coord guard stops the loop
  // when current === next.
  findNextInheritanceLevel(variant, size) {
    const key = `${variant}${size ? `.${size}` : ""}`;
    const inheritanceLevels = ButtonBranding.inheritanceStructure.inherits[key] || [];
    const nextLevel = inheritanceLevels.length > 0
      ? inheritanceLevels[inheritanceLevels.length - 1]
      : ButtonBranding.inheritanceStructure.base;
    const [nextVariant, nextType] = nextLevel.split(".");
    return { variant: nextVariant, type: nextType ?? null };
  }

  determineType() {
    return "ButtonBranding";
  }

  determineVariant(elementConfig, breakpoint, state, availableBreakpoints) {
    const type = this.processInheritance(elementConfig.options, "button-type", breakpoint, state, availableBreakpoints);
    return type || "primary";
  }

  determineSize(elementConfig, breakpoint, state, availableBreakpoints) {
    const size = this.processInheritance(elementConfig.options, "button-size", breakpoint, state, availableBreakpoints);
    return size || "standard";
  }
}
